/**
 * Base component class.
 *
 * Lifecycle:
 *   new Component(el, props)  → construct
 *   .mount()                  → render template + bind events
 *   .update(newProps)         → merge props + re-mount
 *   .destroy()                → run cleanups + clear DOM
 *
 * Subclasses override:
 *   template()   → returns HTML string
 *   afterRender()→ called after innerHTML is set (e.g. init canvases)
 *   bindEvents() → attach event listeners (use this.addCleanup() to clean up)
 */
export class Component {
  #mounted = false;
  #cleanups = [];

  /**
   * @param {HTMLElement|string} el  Container element or its id.
   * @param {object} props
   */
  constructor(el, props = {}) {
    this.el = typeof el === 'string' ? document.getElementById(el) : el;
    this.props = { ...props };
  }

  /** @returns {string} HTML string */
  // eslint-disable-next-line class-methods-use-this
  template() { return ''; }

  afterRender() {}
  bindEvents() {}

  mount() {
    if (!this.el) return this;
    this.#runCleanups();
    this.el.innerHTML = this.template();
    this.afterRender();
    this.bindEvents();
    this.#mounted = true;
    return this;
  }

  update(props = {}) {
    this.props = { ...this.props, ...props };
    if (this.#mounted) this.mount();
    return this;
  }

  destroy() {
    this.#runCleanups();
    if (this.el) this.el.innerHTML = '';
    this.#mounted = false;
  }

  /** Register a cleanup function to be called before the next render or on destroy. */
  addCleanup(fn) { this.#cleanups.push(fn); }

  /** querySelector scoped to this component's root element. */
  $(selector) { return this.el?.querySelector(selector) ?? null; }

  /** querySelectorAll scoped to this component's root element. */
  $$(selector) { return this.el ? [...this.el.querySelectorAll(selector)] : []; }

  /** Emit a CustomEvent that bubbles from this component's root. */
  emit(name, detail = {}) {
    this.el?.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  get isMounted() { return this.#mounted; }

  #runCleanups() {
    this.#cleanups.forEach(fn => { try { fn(); } catch { /* ignore cleanup errors */ } });
    this.#cleanups = [];
  }
}
